// #region Imports
import { createAsyncThunk, createSelector, createSlice, SerializedError } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import _ from "lodash";
import { companiesApi } from "marketplace/companies/_api/company-api";
import MarketplaceCompanySearchSchema, {
  MarketplaceCompanySearchJSON,
} from "marketplace/companies/_api/_schema/marketplace-company-search-schema";
import Company from "marketplace/companies/company";
import { CompanyJSON } from "marketplace/companies/company-schema";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import { ForumJSON } from "messaging/forums/_schemas/forum-schema";
// #endregion

// #region Store State Type
export type CompanyStoreState = {
  vendors: {
    entries?: CompanyJSON[];
    loading: boolean;
    error: SerializedError | null;
  };
  byId: {
    entries: Record<string, CompanyJSON>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  byQuery: {
    entries: Record<string, CompanyJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  avatars: {
    entries: Record<string, string>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  contactForums: {
    entries: Record<string, ForumJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};
// #endregion

// #region Initial State
const initialState: CompanyStoreState = {
  vendors: {
    entries: undefined,
    loading: false,
    error: null,
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  avatars: {
    entries: {},
    loading: {},
    error: {},
  },
  contactForums: {
    entries: {},
    loading: {},
    error: {},
  },
};
// #endregion

// #region Async Thunks
export const populateCompanyVendors = createAsyncThunk<CompanyJSON[], void>(
  "companies/getCompanyVendors",
  async (_arg, thunkAPI) => {
    try {
      return await thunkAPI.dispatch(companiesApi.endpoints.getCompanyVendors.initiate()).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateCompanyById = createAsyncThunk(
  "companies/getCompanyById",
  async ({ companyId }: { companyId: Guid }, thunkAPI) => {
    try {
      return await thunkAPI
        .dispatch(companiesApi.endpoints.getCompanyById.initiate({ companyId: companyId.value }))
        .unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
  {
    condition: ({ companyId }, { getState }) => {
      const state = getState() as RootState;
      if (state.companies.byId.entries[companyId.value]) return false;
    },
  },
);

export const populateCompaniesByIds = createAsyncThunk<Company[], { ids: Guid[] }>(
  "companies/populateByIds",
  async ({ ids }: { ids: Guid[] }, thunkAPI) => {
    if (!ids.length) return [];

    const store = thunkAPI.getState() as RootState;
    const missingCompanyIds = _.uniqWith(
      ids.filter((id) => !store.companies.byId.entries[id.value]),
      (a, b) => a.isEqualTo(b),
    );

    if (!missingCompanyIds.length) return [];

    try {
      const result = await thunkAPI
        .dispatch(
          companiesApi.endpoints.getCompaniesByIds.initiate({ ids: missingCompanyIds.map((id) => id.value) }),
        )
        .unwrap();
      if (!result.length) return [];
      return Company.fromJSONArray(result) ?? [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateCompanyAvatar = createAsyncThunk(
  "companies/getCompanyAvatar",
  async ({ companyId }: { companyId: Guid }, thunkAPI) => {
    try {
      return await thunkAPI
        .dispatch(companiesApi.endpoints.getCompanyAvatar.initiate({ companyId: companyId.value }))
        .unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const populateCompanyContactForums = createAsyncThunk(
  "companies/getCompanyContactForums",
  async ({ companyId }: { companyId: Guid }, thunkAPI) => {
    try {
      return await thunkAPI
        .dispatch(companiesApi.endpoints.getCompanyContactForums.initiate({ companyId: companyId.value }))
        .unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createCompanyAvatar = createAsyncThunk(
  "companies/createCompanyAvatar",
  async ({ companyId, avatar }: { companyId: Guid; avatar: File }, thunkAPI) => {
    try {
      await thunkAPI
        .dispatch(companiesApi.endpoints.createCompanyAvatar.initiate({ companyId: companyId.value, file: avatar }))
        .unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const createCompany = createAsyncThunk(
  "companies/createCompany",
  async ({ company }: { company: Company }, thunkAPI) => {
    try {
      return await thunkAPI.dispatch(companiesApi.endpoints.createCompany.initiate({ company })).unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const updateCompany = createAsyncThunk(
  "companies/updateCompany",
  async (
    {
      companyId,
      isVisible,
      contactId,
      originalProfile,
      updatedProfile,
    }: {
      companyId: Guid;
      isVisible: boolean;
      contactId?: Guid;
      originalProfile?: CompanyProfile;
      updatedProfile?: CompanyProfile;
    },
    thunkAPI,
  ) => {
    try {
      return await thunkAPI
        .dispatch(
          companiesApi.endpoints.updateCompany.initiate({
            companyId: companyId.value,
            isVisible,
            contactId,
            originalProfile,
            updatedProfile,
          }),
        )
        .unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const getCompanyContactForum = createAsyncThunk<ForumJSON, { companyId: Guid }>(
  "companies/contactCompany",
  async ({ companyId }: { companyId: Guid }, thunkAPI) => {
    try {
      return await thunkAPI
        .dispatch(companiesApi.endpoints.getCompanyContactForum.initiate({ companyId: companyId.value }))
        .unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);
// #endregion

// #region Slice
export const companiesSlice = createSlice({
  name: "companies",
  initialState,
  // #region Simple Reducers
  reducers: {
    addCompany: (state, action) => {
      const companyId = action.payload.id?.value;
      if (!companyId) return;

      state.byId.entries[companyId] = action.payload;
      state.vendors.entries = state.vendors.entries?.filter((company) => company.id !== companyId) ?? [];
      state.vendors.entries.push(action.payload);
    },
    addCompanyJSON: (state, action: { payload: CompanyJSON }) => {
      const companyJSON = action.payload;
      if (!companyJSON.id) return;

      state.byId.entries[companyJSON.id] = companyJSON;
      state.vendors.entries = state.vendors.entries?.filter((company) => company.id !== companyJSON.id) ?? [];
      state.vendors.entries.push(companyJSON);
    },
    removeCompany: (state, action) => {
      delete state.byId.entries[action.payload.value];
    },
  },
  // #endregion
  // #region Async Reducers
  extraReducers: (builder) => {
    // getCompanyVendors
    builder.addMatcher(companiesApi.endpoints.getCompanyVendors.matchPending, (state) => {
      state.vendors.loading = true;
      state.vendors.error = null;
    });
    builder.addMatcher(companiesApi.endpoints.getCompanyVendors.matchFulfilled, (state, action) => {
      state.vendors.entries = action.payload;
      state.vendors.loading = false;
    });
    builder.addMatcher(companiesApi.endpoints.getCompanyVendors.matchRejected, (state, action) => {
      state.vendors.loading = false;
      state.vendors.error = action.error;
    });

    // getCompanyById
    builder.addMatcher(companiesApi.endpoints.getCompanyById.matchPending, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.byId.loading[companyId] = true;
      state.byId.error[companyId] = null;
    });
    builder.addMatcher(companiesApi.endpoints.getCompanyById.matchFulfilled, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.byId.loading[companyId] = false;
      if (!action.payload) {
        console.error("Company not received by store.");
        return;
      }
      state.byId.entries[companyId] = action.payload;
    });
    builder.addMatcher(companiesApi.endpoints.getCompanyById.matchRejected, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.byId.loading[companyId] = false;
      state.byId.error[companyId] = action.error;
    });

    // getCompaniesByIds
    builder.addMatcher(companiesApi.endpoints.getCompaniesByIds.matchPending, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      if (!ids.length) return;
      for (const id of ids) {
        state.byId.loading[id] = true;
        state.byId.error[id] = null;
      }
    });
    builder.addMatcher(companiesApi.endpoints.getCompaniesByIds.matchFulfilled, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      if (!ids.length) return;
      for (const company of action.payload) {
        state.byId.entries[company.id] = company;
      }
      for (const id of ids) {
        state.byId.loading[id] = false;
      }
    });
    builder.addMatcher(companiesApi.endpoints.getCompaniesByIds.matchRejected, (state, action) => {
      const ids = action.meta.arg.originalArgs.ids;
      if (!ids.length) return;
      for (const id of ids) {
        state.byId.loading[id] = false;
        state.byId.error[id] = action.error;
      }
    });

    // searchCompanies
    builder.addMatcher(companiesApi.endpoints.searchCompanies.matchPending, (state, action) => {
      const cacheKey = MarketplaceCompanySearchSchema.toCacheKey(action.meta.arg.originalArgs.search);
      state.byQuery.loading[cacheKey] = true;
      state.byQuery.error[cacheKey] = null;
    });
    builder.addMatcher(companiesApi.endpoints.searchCompanies.matchFulfilled, (state, action) => {
      const cacheKey = MarketplaceCompanySearchSchema.toCacheKey(action.meta.arg.originalArgs.search);
      state.byQuery.entries[cacheKey] =
        action.payload.hits?.map((hit) => ({
          ...hit.data,
          id: hit.data.id ?? "",
          legalEntityId: hit.data.legalEntityId ?? "",
        })) ?? [];
      state.byQuery.loading[cacheKey] = false;
    });
    builder.addMatcher(companiesApi.endpoints.searchCompanies.matchRejected, (state, action) => {
      const cacheKey = MarketplaceCompanySearchSchema.toCacheKey(action.meta.arg.originalArgs.search);
      state.byQuery.loading[cacheKey] = false;
      state.byQuery.error[cacheKey] = action.error;
    });

    // getCompanyAvatar
    builder.addMatcher(companiesApi.endpoints.getCompanyAvatar.matchPending, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.avatars.loading[companyId] = true;
      state.avatars.error[companyId] = null;
    });
    builder.addMatcher(companiesApi.endpoints.getCompanyAvatar.matchFulfilled, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.avatars.entries[companyId] = action.payload;
      state.avatars.loading[companyId] = false;
    });
    builder.addMatcher(companiesApi.endpoints.getCompanyAvatar.matchRejected, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.avatars.loading[companyId] = false;
      state.avatars.error[companyId] = action.error;
    });

    // getCompanyContactForums
    builder.addMatcher(companiesApi.endpoints.getCompanyContactForums.matchPending, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.contactForums.loading[companyId] = true;
      state.contactForums.error[companyId] = null;
    });
    builder.addMatcher(companiesApi.endpoints.getCompanyContactForums.matchFulfilled, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.contactForums.entries[companyId] = action.payload;
      state.contactForums.loading[companyId] = false;
    });
    builder.addMatcher(companiesApi.endpoints.getCompanyContactForums.matchRejected, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.contactForums.loading[companyId] = false;
      state.contactForums.error[companyId] = action.error;
    });

    // createCompanyAvatar
    builder.addMatcher(companiesApi.endpoints.createCompanyAvatar.matchPending, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.avatars.loading[companyId] = true;
      state.avatars.error[companyId] = null;
    });
    builder.addMatcher(companiesApi.endpoints.createCompanyAvatar.matchFulfilled, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.avatars.loading[companyId] = false;
      state.avatars.entries[companyId] = URL.createObjectURL(action.meta.arg.originalArgs.file);
    });
    builder.addMatcher(companiesApi.endpoints.createCompanyAvatar.matchRejected, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.avatars.loading[companyId] = false;
      state.avatars.entries[companyId] = "";
      state.avatars.error[companyId] = action.error;
    });

    // createCompany
    builder.addMatcher(companiesApi.endpoints.createCompany.matchPending, (state, action) => {
      const companyId = action.meta.arg.originalArgs.company.id.value;
      state.byId.loading[companyId] = true;
      state.byId.error[companyId] = null;
    });
    builder.addMatcher(companiesApi.endpoints.createCompany.matchFulfilled, (state, action) => {
      const companyId = action.payload.id;
      state.byId.loading[companyId] = false;
      state.byId.entries[companyId] = action.payload;
    });
    builder.addMatcher(companiesApi.endpoints.createCompany.matchRejected, (state, action) => {
      const companyId = action.meta.arg.originalArgs.company.id.value;
      state.byId.loading[companyId] = false;
      state.byId.error[companyId] = action.error;
    });

    // updateCompany
    builder.addMatcher(companiesApi.endpoints.updateCompany.matchPending, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.byId.loading[companyId] = true;
      state.byId.error[companyId] = null;
    });
    builder.addMatcher(companiesApi.endpoints.updateCompany.matchFulfilled, (state, action) => {
      const companyId = action.payload.id;
      state.byId.loading[companyId] = false;
      state.byId.entries[companyId] = action.payload;
      state.byQuery = { entries: {}, loading: {}, error: {} };
      state.vendors.entries = undefined;
    });
    builder.addMatcher(companiesApi.endpoints.updateCompany.matchRejected, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.byId.loading[companyId] = false;
      state.byId.error[companyId] = action.error;
    });
  },
  // #endregion
});
// #endregion

// #region Exports

export const { addCompany, addCompanyJSON, removeCompany } = companiesSlice.actions;

// #region Company Vendors
const selectCompanyVendors = createSelector([(state: RootState) => state.companies.vendors.entries], (entries) =>
  !entries ? undefined : entries.map((json) => Company.fromJSON(json)).filter((c): c is Company => !!c),
);
export const getCompanyVendors = () => (state: RootState) => selectCompanyVendors(state);

export const getCompanyVendorsIsLoading = () => (state: RootState) => state.companies.vendors.loading;

export const getCompanyVendorsError = () => (state: RootState) => state.companies.vendors.error;
// #endregion

// #region Company By Id
const selectCompanyById = createSelector(
  [(state: RootState) => state.companies.byId.entries, (_: RootState, id?: Guid) => id],
  (entries, id) => {
    if (!id) return undefined;
    const companyJson = entries[id.value];
    if (!companyJson) return undefined;
    return Company.fromJSON(companyJson);
  },
);
export const getCompanyById = (id?: Guid) => (state: RootState) => selectCompanyById(state, id);

export const getIsLoadingCompanyById = (id?: Guid) => (state: RootState) =>
  id ? state.companies.byId.loading[id.value] : false;

export const getErrorLoadingCompanyById = (id?: Guid) => (state: RootState) =>
  id ? state.companies.byId.error[id.value] : undefined;
// #endregion

// #region Companies By Ids
const selectCompaniesByIds = createSelector(
  [(state: RootState) => state.companies.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) =>
    ids
      .map((id) => entries[id.value])
      .filter((x): x is CompanyJSON => !!x)
      .map((json) => Company.fromJSON(json)),
);
export const getCompaniesByIds = (ids: Guid[]) => (state: RootState) => selectCompaniesByIds(state, ids);

const selectCompaniesLoadingByIds = createSelector(
  [(state: RootState) => state.companies.byId.loading],
  (loadingMap: Record<string, boolean>) => {
    return Object.keys(loadingMap)
      .map((idValue) => Guid.fromJSON(idValue))
      .filter((id) => id instanceof Guid);
  },
);
export const getLoadingCompaniesByIds = () => (state: RootState) => selectCompaniesLoadingByIds(state);

const selectCompanyErrorsByIds = createSelector(
  [(state: RootState) => state.companies.byId.error],
  (errorMap: Record<string, SerializedError | null>) => {
    return Object.keys(errorMap)
      .map((idValue) => Guid.fromJSON(idValue))
      .filter((id) => id instanceof Guid);
  },
);
export const getErrorLoadingCompaniesByIds = () => (state: RootState) => selectCompanyErrorsByIds(state);
// #endregion

// #region Companies By Query
export const getCompaniesByQuery = (search: MarketplaceCompanySearchJSON) => (state: RootState) =>
  state.companies.byQuery.entries[MarketplaceCompanySearchSchema.toCacheKey(search)];

export const getIsLoadingCompaniesByQuery = (search: MarketplaceCompanySearchJSON) => (state: RootState) =>
  state.companies.byQuery.loading[MarketplaceCompanySearchSchema.toCacheKey(search)];

export const getErrorLoadingCompaniesByQuery = (search: MarketplaceCompanySearchJSON) => (state: RootState) =>
  state.companies.byQuery.error[MarketplaceCompanySearchSchema.toCacheKey(search)];
// #endregion

// #region Company Avatar
export const getCompanyAvatar = (companyId: Guid | null) => (state: RootState) =>
  companyId ? state.companies.avatars.entries[companyId.value] : undefined;

export const getCompanyAvatarIsLoading = (companyId: Guid | null) => (state: RootState) =>
  companyId ? state.companies.avatars.loading[companyId.value] : false;

export const getCompanyAvatarError = (companyId: Guid | null) => (state: RootState) =>
  companyId ? state.companies.avatars.error[companyId.value] : undefined;
// #endregion

// #endregion
